import React, { useState, useContext } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  Text,
  useToast,
  Spinner,
  Box,
  Icon,
  Divider,
  Badge,
  HStack,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../../../../../context/AuthProvider';
import { axiosWrapper } from '../../../../../../hooks/axios';
import {
  useManagedSchoolsQuery,
  ManagedSchoolsQuery,
} from '../../../../../../generated/graphqlModels';
import { AddIcon, ViewIcon, StarIcon } from '@chakra-ui/icons';
import { CLASSROOM_CONFIG } from '../config';

interface CreateClassroomModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateClassroomModal: React.FC<CreateClassroomModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const user = useContext(AuthContext);
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    school_id: '',
    program_id: '',
  });

  const { data: schoolsData } = useManagedSchoolsQuery();
  const bgColor = '#ffffff';
  const borderColor = '#e2e8f0';

  // filter schools where user is teacher/manager and in allowed namespaces
  const eligibleSchools = schoolsData?.managedSchools?.filter((school) => {
    const hasUserAffiliation = school.affiliation && 
      (school.affiliation.isTeacher || school.affiliation.isManager);
    const isAllowedNamespace = CLASSROOM_CONFIG.ALLOWED_NAMESPACES.includes(
      (school as unknown as { namespace: string }).namespace
    );    return hasUserAffiliation && isAllowedNamespace;
  }) || [];

  // auto school if user is affiliated with only one school
  React.useEffect(() => {
    if (eligibleSchools.length === 1 && !formData.school_id && eligibleSchools[0]?.id) {
      setFormData(prev => ({ ...prev, school_id: eligibleSchools[0]!.id }));
    }
  }, [eligibleSchools, formData.school_id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.school_id || !formData.program_id) {
      toast({
        title: t('classroom.pleaseFillAllFields'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await axiosWrapper('/api/v1/records/classrooms/create/', 'post', formData);
      
      toast({
        title: t('classroom.createdSuccessfully'),
        description: t('classroom.createdDescription', { name: formData.name }),
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // reset form
      setFormData({ name: '', school_id: '', program_id: '' });
      onClose();
      onSuccess?.();
      
    } catch (error: any) {
      console.error('Error creating classroom:', error);
      toast({
        title: t('classroom.failedToCreate'),
        description: t('classroom.somethingWentWrong'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // don't show modal if user has no eligible schools
  if (!eligibleSchools.length) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent 
        bg={bgColor}
        borderRadius="2xl"
        boxShadow="2xl"
        maxW="600px"
      >
        <ModalHeader 
          pb={4}
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <HStack spacing={3}>
            <Icon as={AddIcon} color="blue.500" w={6} h={6} />
            <VStack align="start" spacing={0}>
              <Text fontSize="xl" fontWeight="bold">
                {t('classroom.createNewClassroom')}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {t('classroom.setupNewClassroom')}
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        
        <form onSubmit={handleSubmit}>
          <ModalBody py={8}>
            <VStack spacing={8} align="stretch">
              {/* Classroom Name Section */}
              <Box>
                <FormControl isRequired>
                  <FormLabel 
                    fontSize="md" 
                    fontWeight="semibold"
                    color="gray.700"
                    mb={3}
                  >
                    {t('classroom.classroomName')}
                  </FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder={t('classroom.classroomNamePlaceholder')}
                    size="lg"
                    borderRadius="xl"
                    border="2px solid"
                    borderColor={borderColor}
                    _focus={{
                      borderColor: '#3182ce',
                      boxShadow: '0 0 0 1px #3182ce',
                    }}
                  />
                </FormControl>
              </Box>

              <Divider />

              {/* School Selection Section */}
              <Box>
                <FormControl isRequired>
                  <HStack spacing={2} mb={3}>
                    <Icon as={ViewIcon} color="green.500" w={5} h={5} />
                    <FormLabel 
                      fontSize="md" 
                      fontWeight="semibold"
                      color="gray.700"
                      mb={0}
                    >
                      {t('classroom.school')}
                    </FormLabel>
                  </HStack>
                  <Select
                    value={formData.school_id}
                    onChange={(e) => handleInputChange('school_id', e.target.value)}
                    placeholder={t('classroom.selectSchoolPlaceholder')}
                    size="lg"
                    borderRadius="xl"
                    border="2px solid"
                    borderColor={borderColor}
                    _focus={{
                      borderColor: '#3182ce',
                      boxShadow: '0 0 0 1px #3182ce',
                    }}
                  >
                    {eligibleSchools.map((school) => (
                      <option key={school.id} value={school.id}>
                        {school.name}
                      </option>
                    ))}
                  </Select>
                  {eligibleSchools.length === 1 && (
                    <HStack mt={2} spacing={2}>
                      <Badge colorScheme="green" variant="subtle">
                        {t('classroom.autoSelected')}
                      </Badge>
                      <Text fontSize="sm" color="gray.500">
                        {t('classroom.onlySchoolYouManage')}
                      </Text>
                    </HStack>
                  )}
                </FormControl>
              </Box>

              <Divider />

              {/* Program Selection Section */}
              <Box>
                <FormControl isRequired>
                  <HStack spacing={2} mb={3}>
                    <Icon as={StarIcon} color="purple.500" w={5} h={5} />
                    <FormLabel 
                      fontSize="md" 
                      fontWeight="semibold"
                      color="gray.700"
                      mb={0}
                    >
                      {t('classroom.program')}
                    </FormLabel>
                  </HStack>
                  <Select
                    value={formData.program_id}
                    onChange={(e) => handleInputChange('program_id', e.target.value)}
                    placeholder={t('classroom.chooseProgramPlaceholder')}
                    size="lg"
                    borderRadius="xl"
                    border="2px solid"
                    borderColor={borderColor}
                    _focus={{
                      borderColor: '#3182ce',
                      boxShadow: '0 0 0 1px #3182ce',
                    }}
                  >
                    {CLASSROOM_CONFIG.AVAILABLE_PROGRAMS.map((program) => (
                      <option key={program.id} value={program.id}>
                        {program.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </VStack>
          </ModalBody>

          <ModalFooter 
            pt={4}
            borderTop="1px solid"
            borderColor={borderColor}
          >
            <HStack spacing={3} w="full" justify="flex-end">
              <Button 
                variant="ghost" 
                onClick={onClose}
                size="lg"
                px={8}
              >
                {t('classroom.cancel')}
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={isLoading}
                loadingText={t('classroom.creating')}
                size="lg"
                px={8}
                borderRadius="xl"
                leftIcon={<AddIcon />}
                _hover={{
                  transform: 'translateY(-1px)',
                  boxShadow: 'lg',
                }}
                transition="all 0.2s"
              >
                {t('classroom.createClassroom')}
              </Button>
            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
